import React from 'react';
import BanterLoader from './BanterLoader';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'neutral';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    fullWidth?: boolean;
    loading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    children: React.ReactNode;
}

/**
 * Bouton ATABLE — pilule, comme les CTA du site.
 *
 * - primary   : rouge ATABLE, l'action principale de l'écran (une seule par vue).
 * - secondary : pêche, action positive secondaire ("Ajouter", "Inviter").
 * - outline   : blanc chaud + liseré, actions neutres et navigation.
 * - ghost     : transparent, actions discrètes (liens, "Tout voir").
 * - neutral   : encre, pour les actions de "mode" (quitter la caisse…).
 * - danger    : liseré rouge sur blanc, distinct du primaire (actions destructives).
 * - success   : état explicite.
 */
const VARIANTS: Record<ButtonVariant, string> = {
    primary:
        'bg-brand-500 text-cream hover:bg-brand-600',
    secondary:
        'bg-peach-100 text-brand-700 hover:bg-peach-200',
    outline:
        'bg-white text-gray-900 border border-gray-400 hover:bg-gray-50',
    ghost:
        'bg-transparent text-gray-700 hover:bg-gray-100 hover:text-gray-900',
    // Distinct du primaire : une action destructive ne doit pas ressembler à l'action principale.
    danger:
        'bg-white text-brand-700 border border-brand-500 hover:bg-brand-50',
    success:
        'bg-success text-cream hover:brightness-95',
    neutral:
        'bg-gray-900 text-cream hover:bg-gray-800',
};

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-9 px-3.5 text-[13px] gap-1.5',
    md: 'h-10 px-4 text-sm gap-2',
    lg: 'h-12 px-5 text-[15px] gap-2',
};

const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    leftIcon,
    rightIcon,
    children,
    disabled,
    className = '',
    type = 'button',
    ...props
}) => {
    const isDisabled = disabled || loading;

    return (
        <button
            type={type}
            className={[
                'inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium leading-none',
                'transition-colors duration-150 ease-out select-none',
                // Focus clavier : contour plein brand-500 décalé de 2 px (contraste ≥ 3:1 sur
                // crème comme sur blanc), au lieu d'un halo à 25-40 % d'opacité peu visible.
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'active:translate-y-px',
                fullWidth ? 'w-full' : '',
                SIZES[size],
                VARIANTS[variant],
                className,
            ].filter(Boolean).join(' ')}
            disabled={isDisabled}
            {...props}
        >
            {loading && <BanterLoader size={size === 'lg' ? 'sm' : 'xs'} />}
            {!loading && leftIcon && <span className="flex-shrink-0 inline-flex">{leftIcon}</span>}
            <span className="leading-none">{children}</span>
            {!loading && rightIcon && <span className="flex-shrink-0 inline-flex">{rightIcon}</span>}
        </button>
    );
};

export default Button;
